import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { toast } from '../components/Toast';

const DAY_NAMES = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const DAY_NAMES_FULL = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];

function getWeekDates(weekOffset = 0) {
  const today = new Date();
  const day = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1) + weekOffset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().split('T')[0];
  });
}

function formatShortDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function isToday(dateStr) {
  return dateStr === new Date().toISOString().split('T')[0];
}

export default function WeekMenu({ onNavigate }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [entries, setEntries] = useState({});
  const [recipes, setRecipes] = useState([]);
  const [editDay, setEditDay] = useState(null);
  const [editForm, setEditForm] = useState({ mode: 'text', custom_dish: '', recipe_id: null, notes: '' });
  const [recipeSearch, setRecipeSearch] = useState('');

  const weekDates = getWeekDates(weekOffset);
  const start = weekDates[0];
  const end = weekDates[6];

  const load = useCallback(async () => {
    const [menuEntries, recipeList] = await Promise.all([
      api.get(`/week-menu?start=${start}&end=${end}`),
      api.get('/recipes'),
    ]);
    const map = {};
    for (const e of menuEntries) map[e.date] = e;
    setEntries(map);
    setRecipes(recipeList);
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (date) => {
    const existing = entries[date];
    if (existing) {
      setEditForm({
        mode: existing.recipe_id ? 'recipe' : 'text',
        custom_dish: existing.custom_dish || '',
        recipe_id: existing.recipe_id || null,
        notes: existing.notes || '',
      });
    } else {
      setEditForm({ mode: 'text', custom_dish: '', recipe_id: null, notes: '' });
    }
    setRecipeSearch('');
    setEditDay(date);
  };

  const handleSave = async () => {
    const body = {
      recipe_id: editForm.mode === 'recipe' ? editForm.recipe_id : null,
      custom_dish: editForm.mode === 'text' ? editForm.custom_dish : '',
      notes: editForm.notes,
    };
    if (!body.recipe_id && !body.custom_dish.trim()) {
      await handleClear(editDay);
      return;
    }
    try {
      await api.put(`/week-menu/${editDay}`, body);
      setEditDay(null);
      load();
      toast('Weekmenu opgeslagen ✓');
    } catch {
      toast('Fout bij opslaan', 'error');
    }
  };

  const handleClear = async (date) => {
    try {
      await api.delete(`/week-menu/${date}`);
      setEditDay(null);
      load();
      toast('Dag gewist');
    } catch {
      toast('Fout bij wissen', 'error');
    }
  };

  const weekLabel = () => {
    if (weekOffset === 0) return 'Deze week';
    if (weekOffset === 1) return 'Volgende week';
    if (weekOffset === -1) return 'Vorige week';
    const d = new Date(start + 'T12:00:00');
    return `Week van ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
  };

  const filteredRecipes = recipes.filter(r =>
    !recipeSearch || r.name.toLowerCase().includes(recipeSearch.toLowerCase())
  );

  const selectedRecipeName = editForm.recipe_id
    ? recipes.find(r => r.id === editForm.recipe_id)?.name
    : null;

  return (
    <div className="content">
      {/* Week navigation */}
      <div className="week-nav">
        <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(w => w - 1)}>‹ Vorige</button>
        <div className="week-nav-label">
          <span>{weekLabel()}</span>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>
            {formatShortDate(start)} – {formatShortDate(end)}
          </span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(w => w + 1)}>Volgende ›</button>
      </div>
      {weekOffset !== 0 && (
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(0)}>↩ Terug naar deze week</button>
        </div>
      )}

      {/* Week days */}
      <div className="week-grid">
        {weekDates.map((date, i) => {
          const entry = entries[date];
          const dishName = entry?.recipe_name || entry?.custom_dish;
          const today = isToday(date);
          return (
            <div
              key={date}
              className={`week-day-card ${today ? 'today' : ''} ${dishName ? 'planned' : ''}`}
              onClick={() => openEdit(date)}
            >
              <div className="week-day-header">
                <span className="week-day-name">{DAY_NAMES[i]}</span>
                <span className="week-day-date">{formatShortDate(date)}</span>
                {today && <span className="week-today-badge">vandaag</span>}
              </div>
              <div className="week-day-dish">
                {dishName ? (
                  <>
                    <span className="week-dish-name">{dishName}</span>
                    {entry?.recipe_id && (
                      <button
                        className="week-dish-linked"
                        title="Bekijk recept"
                        onClick={e => { e.stopPropagation(); onNavigate('recipes', entry.recipe_id); }}
                      >
                        🔗
                      </button>
                    )}
                  </>
                ) : (
                  <span className="week-day-empty">+ Plannen</span>
                )}
              </div>
              {entry?.notes && (
                <div className="week-day-notes">{entry.notes}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit modal */}
      {editDay && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditDay(null)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3>
                {DAY_NAMES_FULL[weekDates.indexOf(editDay)]} · {formatShortDate(editDay)}
              </h3>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setEditDay(null)}>✕</button>
            </div>
            <div className="modal-body">
              {/* Mode toggle */}
              <div className="filter-row" style={{ marginBottom: 16 }}>
                <button
                  className={`filter-pill ${editForm.mode === 'text' ? 'active' : ''}`}
                  onClick={() => setEditForm(f => ({ ...f, mode: 'text', recipe_id: null }))}
                >
                  Vrije tekst
                </button>
                <button
                  className={`filter-pill ${editForm.mode === 'recipe' ? 'active' : ''}`}
                  onClick={() => setEditForm(f => ({ ...f, mode: 'recipe', custom_dish: '' }))}
                >
                  Uit recepten
                </button>
              </div>

              {editForm.mode === 'text' ? (
                <div className="form-group">
                  <label className="form-label">Gerecht</label>
                  <input
                    className="form-input"
                    placeholder="Naam van het gerecht..."
                    value={editForm.custom_dish}
                    onChange={e => setEditForm(f => ({ ...f, custom_dish: e.target.value }))}
                    autoFocus
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Recept kiezen</label>
                  {selectedRecipeName && (
                    <div className="week-selected-recipe">
                      <span>🍽️ {selectedRecipeName}</span>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditForm(f => ({ ...f, recipe_id: null }))}>✕</button>
                    </div>
                  )}
                  <div className="search-bar" style={{ marginBottom: 8, marginTop: selectedRecipeName ? 8 : 0 }}>
                    <span className="search-icon">🔍</span>
                    <input
                      placeholder="Zoek recept..."
                      value={recipeSearch}
                      onChange={e => setRecipeSearch(e.target.value)}
                    />
                  </div>
                  <div className="week-recipe-list">
                    {filteredRecipes.length === 0 ? (
                      <div style={{ padding: '12px 0', color: 'var(--text3)', fontSize: 13, textAlign: 'center' }}>Geen recepten gevonden</div>
                    ) : filteredRecipes.map(r => (
                      <button
                        key={r.id}
                        className={`week-recipe-item ${editForm.recipe_id === r.id ? 'active' : ''}`}
                        onClick={() => setEditForm(f => ({ ...f, recipe_id: r.id }))}
                      >
                        <span>{r.name}</span>
                        {editForm.recipe_id === r.id && <span style={{ color: 'var(--ice)' }}>✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Notitie (optioneel)</label>
                <input
                  className="form-input"
                  placeholder="Extra info..."
                  value={editForm.notes}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              {entries[editDay] && (
                <button className="btn btn-danger btn-sm" onClick={() => handleClear(editDay)}>Wissen</button>
              )}
              <div style={{ flex: 1 }} />
              {editForm.mode === 'recipe' && editForm.recipe_id && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => { setEditDay(null); onNavigate('recipes', editForm.recipe_id); }}
                >
                  🔗 Bekijk recept
                </button>
              )}
              <button className="btn btn-ghost" onClick={() => setEditDay(null)}>Annuleren</button>
              <button className="btn btn-primary" onClick={handleSave}>Opslaan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
